import type { PropType, Directive } from 'vue';
import { getCurrentInstance } from 'vue';
import type { LocationQuery, RouteRecordName } from 'vue-router';
import kebabCase from '@/common/kebabCase';
import type { ZvdZLocationRaw } from '@/plugins/router';
import router from '@/plugins/router';
import { RouteName } from '@/router/route-names';
import excludeFalsy from '@/common/exclude-falsy';

type TestId = string | number | undefined | null | false | ZvdZLocationRaw;

type NestedArray<T> = Array<NestedArray<T> | T>;

export type TestIdInput = TestId | NestedArray<TestId>;

export const attrName = 'data-test-id';

const getRouteId = (
  name: string | RouteRecordName,
  query?: LocationQuery | null,
) => {
  const queryId = Object.entries(query || {})
    .filter(([key]) => !key.endsWith('Id'))
    .map(([, value]) =>
      [...(Array.isArray(value) ? value : [value])].map(String).join('-'),
    );
  return `${String(name)}-${queryId}`;
};

const flatten = <T>(arr: T[][]): T[] => ([] as T[]).concat(...arr);
const flattenDeep = <T>(input: NestedArray<T>): T[] =>
  flatten(input.map(x => (Array.isArray(x) ? flattenDeep(x) : [x])));

export const getTestIdString = (input: TestIdInput = ''): string =>
  excludeFalsy(
    flattenDeep<TestId>([input]).map(item => {
      if (!item) {
        return false;
      }
      if (typeof item === 'string') {
        if (item.startsWith('/')) {
          const route = router.resolve(item);
          if (route.name && route.name !== RouteName.CatchAll) {
            return getRouteId(route.name, route.query);
          }
        }
        if (item.match(/^https?:\/\//)) {
          return `external-link__${item.replace(/^https?:\/\/(.*)/, '$1')}`;
        }
      }
      if (typeof item === 'object' && typeof item.name === 'string') {
        return item.name;
      }
      return String(item);
    }),
  )
    .map(part => part.split('__'))
    .flat()
    .map(part => kebabCase(part))
    .join('__');

export const getActionableElement = (
  el: Element,
  selector?: string,
): Element => (selector ? el.querySelector(selector) || el : el);

const setAttribute = (
  el: HTMLElement,
  { value, arg }: { value?: unknown; arg?: string },
): void => {
  const id = getTestIdString(value as TestIdInput);
  if (!id) {
    getActionableElement(el, arg).removeAttribute(attrName);
    return;
  }
  getActionableElement(el, arg).setAttribute(attrName, id);
};

const getListener = (name: string): undefined | (() => unknown) => {
  const self = getCurrentInstance();
  return (self?.vnode.props || {})[`onUpdate:${name}`] as
    | undefined
    | (() => unknown);
};

function useGetModelExpression(name = 'modelValue'): string | undefined {
  const listener = getListener(name)?.toString();
  const hasEvent = listener?.includes('$event');
  if (!(listener && hasEvent)) {
    return undefined;
  }
  const re = /\(\$event\) => \S+\.([\w.]+) = \$event/;
  const model = re.exec(listener);

  // We only want to match 'Group 1' from the Regex
  if (!(model && model.length > 1)) {
    return undefined;
  }
  return model[1];
}

export function getModelExpression(): string {
  return useGetModelExpression() || '';
}

export type ActionableComponentProps = {
  testId?: TestIdInput;
};

export const actionableComponentProps: { testId: PropType<TestIdInput> } = {
  testId: [String, Object, Array] as PropType<TestIdInput>,
};

export const vSetTestId: Directive = {
  mounted: setAttribute,
  updated: setAttribute,
};
